/**
 * useVatCheckoutGate — SW-8538
 *
 * Composite hook. Combines the geolocation + classification hooks into a
 * single value used by the Upgrade modal to decide whether to show Step 2.
 *
 * `showVatStep === true` when:
 *   - geolocation resolved AND
 *   - country is in the EU VAT set (DE FR NL ES IT SE BE AT)
 *
 * `showVatStep === false` for non-EU, geolocation errors, or while loading.
 * Fail-closed on geolocation error — better to charge normally than to
 * present an incorrect VAT step.
 *
 * See docs/vat-project/SW-8538-PRODUCTION-PLAN.md §4 + §6.
 */
import useVatCountry from "./useVatCountry";
import useVatClassification from "./useVatClassification";

/**
 * @returns {{
 *   showVatStep: boolean,
 *   country: string | null,
 *   classification: "business" | "consumer",
 *   source: string,
 *   isLoading: boolean,
 * }}
 */
export default function useVatCheckoutGate() {
  const { country, isEU, isLoading: geoLoading } = useVatCountry();
  const { classification, source, isLoading: classLoading } = useVatClassification();

  const isLoading = Boolean(geoLoading || classLoading);

  // Fail-closed: still-loading or non-EU → don't show VAT step. A failed
  // geo lookup surfaces as `country: null` → `isEU: false` from
  // useVatCountry (it swallows errors by design), so we don't need a
  // separate error term here.
  const showVatStep = !isLoading && isEU;

  return {
    showVatStep,
    country,
    classification,
    source,
    isLoading,
  };
}
